import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Paper, 
  Typography, 
  Button, 
  CircularProgress, 
  Alert,
  AppBar,
  Toolbar,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  PlayArrow as PlayIcon,
  FileCopy as FileCopyIcon
} from '@mui/icons-material';
import api from '../api/axios';
import flowStepsApi from '../api/flowStepsApi';
import templatesApi from '../api/templatesApi';
import BreadcrumbsNav from '../components/BreadcrumbsNav';
import VisualFlowEditor, { TestFlowDialog } from '../components/VisualFlowEditor';
import FlowErrorBoundary from '../components/FlowErrorBoundary';

const FlowDesignerPage = () => {
  const { companyId, projectId } = useParams();
  const navigate = useNavigate();
  
  // Mobile responsive
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // < 900px
  
  // State management
  const [project, setProject] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [testFlowOpen, setTestFlowOpen] = useState(false);
  const [loadTemplateOpen, setLoadTemplateOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [applyingTemplate, setApplyingTemplate] = useState(false);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    description: '',
    category: 'custom',
    tags: '',
    availability: 'current_company',
    sharedCompanyIds: []
  });
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const flushPendingRef = useRef(null);
  const [flowData, setFlowData] = useState({
    steps: [],
    connections: [],
    variables: [],
    settings: {
      allowFileUpload: true,
      allowApiInput: false,
      maxFileSize: 20,
      supportedFileTypes: ['pdf', 'png', 'jpg', 'jpeg', 'xlsx', 'docx']
    }
  });

  // Available data for the flow editor
  const [availableVariables, setAvailableVariables] = useState([]);
  const [availableApiEndpoints, setAvailableApiEndpoints] = useState([]);
  const [availableIntents, setAvailableIntents] = useState([]);

  // Function to extract variables from flow steps
  const extractVariablesFromFlow = useCallback((flowData) => {
    const variables = new Set();
    
    if (!flowData?.steps) return [];
    
    flowData.steps.forEach(step => {
      const config = step.config || {};
      
      switch (step.type) {
        case 'data_processing': {
          // Document OCR & Vision creates output variables (default: extractedText)
          const outputVar = config.outputVariable || 'extractedText';
          variables.add(outputVar);

          // Smart Document mode also produces _sheets and (when templates are enabled) _templateExtracted
          const isSmartDoc = config.excelCsvOutputFormat === 'smart-document' ||
                             config.dataProcessingConfig?.excelCsvOutputFormat === 'smart-document';
          if (isSmartDoc) {
            variables.add(`${outputVar}_sheets`);
            if (config.useExcelTemplates || config.dataProcessingConfig?.useExcelTemplates) {
              variables.add(`${outputVar}_templateExtracted`);
            }
          }
          break;
        }
          
        case 'ai_extraction':
          // AI Field Mapping creates variables from extraction fields
          if (config.extractionFields) {
            config.extractionFields.forEach(field => {
              if (field.variableName) {
                variables.add(field.variableName);
              }
            });
          }
          break;
          
        case 'enhanced_api_call':
          // API calls create variables from response mappings
          if (config.responseMappings) {
            config.responseMappings.forEach(mapping => {
              if (mapping.variableName) {
                variables.add(mapping.variableName);
              }
            });
          }
          break;
          
        case 'variable_operations':
          // Variable operations can create new variables
          if (config.operations) {
            config.operations.forEach(operation => {
              if (operation.targetVariable) {
                variables.add(operation.targetVariable);
              }
            });
          }
          break;
          
        case 'advanced_collect_input':
          // Collect input creates variables from form fields
          if (config.fields) {
            config.fields.forEach(field => {
              if (field.variableName || field.name) {
                variables.add(field.variableName || field.name);
              }
            });
          }
          
          // Check for data source variables in table configurations
          if (config.dataSource) {
            variables.add(config.dataSource);
          }
          
          // Check for barcode scanner output variable
          if (config.interfaceType === 'barcode_scanner' && config.outputVariable) {
            variables.add(config.outputVariable);
          }
          
          // Check for file upload output variable
          if (config.interfaceType === 'file_upload' && config.outputVariable) {
            variables.add(config.outputVariable);
          }
          
          // Check for table_builder output variable
          if (config.interfaceType === 'table_builder' && config.outputVariable) {
            variables.add(config.outputVariable);
          }
          
          // Check for general file upload configuration (when enabled on other interfaces)
          if (config.allowFileUpload && config.fileUploadOutputVariable) {
            variables.add(config.fileUploadOutputVariable);
          }
          
          // Check for header field output variables
          if (config.headerFields) {
            config.headerFields.forEach(field => {
              if (field.outputVariable) {
                variables.add(field.outputVariable);
              }
            });
          }
          break;
          
        case 'external_data_source':
          // External Data Source creates variables from response mapping
          if (config.responseMapping) {
            // Add the extracted data variable (default: 'externalData')
            if (config.responseMapping.extractedData) {
              variables.add(config.responseMapping.extractedData);
            }
            // Add the count variable (default: 'dataCount')
            if (config.responseMapping.totalRows) {
              variables.add(config.responseMapping.totalRows);
            }
          }
          // Also add the full response object variable
          variables.add('externalDataResult');
          break;
      }
    });
    
    // Add some common/default variables that are typically available
    variables.add('extractedText');
    variables.add('lineItems');
    variables.add('products');
    variables.add('extractedItems');
    variables.add('invoiceData');
    variables.add('companyName');
    variables.add('totalAmount');
    variables.add('invoiceDate');
    
    return Array.from(variables).sort();
  }, []);

  // Update available variables when flow data changes
  useEffect(() => {
    const extractedVariables = extractVariablesFromFlow(flowData);
    console.log('🔍 Updating available variables:', extractedVariables);
    setAvailableVariables(extractedVariables);
  }, [flowData, extractVariablesFromFlow]);

  // Load project and flow data
  const fetchProjectData = async () => {
    setLoading(true);
    setError('');
    try {
      // Fetch company info
      try {
        const companyRes = await api.get(`/companies/${companyId}`);
        setCompany(companyRes.data);
      } catch (companyErr) {
        console.log('Company endpoint not implemented, using generic data');
        setCompany({
          _id: companyId,
          name: `Company ${companyId.slice(-6)}`,
          primaryColor: '#667eea'
        });
      }

      try {
        const companiesRes = await api.get('/companies');
        setAvailableCompanies(Array.isArray(companiesRes.data) ? companiesRes.data : []);
      } catch (companiesErr) {
        console.log('Companies endpoint not available for template sharing:', companiesErr.message);
        setAvailableCompanies([]);
      }

      // Fetch project details
      try {
        const projectRes = await api.get(`/companies/${companyId}/projects/${projectId}`);
        const projectData = projectRes.data;
        setProject(projectData);
        
        // Load flow data from FlowStep API
        try {
          const flowData = await flowStepsApi.getFlowData(projectId);
          
          // Ensure all steps have the enabled field (for backward compatibility)
          const normalizedSteps = (flowData.steps || []).map(step => ({
            ...step,
            enabled: step.enabled !== undefined ? step.enabled : true
          }));
          
          setFlowData({
            steps: normalizedSteps,
            connections: flowData.connections || [],
            variables: flowData.variables || [],
            settings: flowData.settings || {
              allowFileUpload: true,
              allowApiInput: false,
              maxFileSize: 20,
              supportedFileTypes: ['pdf', 'png', 'jpg', 'jpeg', 'xlsx', 'docx']
            }
          });
        } catch (flowError) {
          console.log('No flow data found, using default:', flowError);
          // Initialize with default flow data if no steps exist
          setFlowData({
            steps: [],
            connections: [],
            variables: [],
            settings: {
              allowFileUpload: true,
              allowApiInput: false,
              maxFileSize: 20,
              supportedFileTypes: ['pdf', 'png', 'jpg', 'jpeg', 'xlsx', 'docx']
            }
          });
        }
      } catch (projectErr) {
        console.log('Project endpoint not implemented, using generic data');
        setProject({
          _id: projectId,
          name: `Project ${projectId.slice(-6)}`,
          description: 'Project description not available',
          status: 'draft',
          isPublished: false,
          companyId,
          flow: {
            version: 1,
            steps: [],
            connections: [],
            variables: [],
            settings: {
              allowFileUpload: true,
              allowApiInput: false,
              maxFileSize: 20,
              supportedFileTypes: ['pdf', 'png', 'jpg', 'jpeg', 'xlsx', 'docx']
            }
          }
        });
      }

      // TODO: Load available API endpoints, variables, etc. from backend
      setAvailableApiEndpoints([
        {
          id: 'sample-api-1',
          name: 'Sample Data Validation API',
          method: 'POST',
          url: 'https://api.example.com/validate',
          description: 'Validates extracted data against business rules',
          headers: { 'Content-Type': 'application/json' },
          parameters: [
            { name: 'data', type: 'object', required: true, description: 'Data to validate' }
          ],
          responseFields: [
            { name: 'isValid', type: 'boolean', description: 'Whether data is valid' },
            { name: 'errors', type: 'array', description: 'List of validation errors' }
          ]
        }
      ]);

      setAvailableIntents([
        { name: 'greeting', description: 'User greetings and welcome messages' },
        { name: 'data_submission', description: 'When user submits data or files' },
        { name: 'help_request', description: 'User asking for help or instructions' },
        { name: 'error_handling', description: 'Handling errors and exceptions' }
      ]);

    } catch (err) {
      setError('Failed to load project data');
      console.error('Flow designer fetch error:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (companyId && projectId) {
      fetchProjectData();
    }
  }, [companyId, projectId]);

  const persistFlowData = async () => {
    const endStep = flowData.steps?.find(s => s.type === 'end');
    if (endStep) {
      console.log('💾 [SAVE] End step config being sent to backend:', {
        enableMultiTab: endStep.config?.enableMultiTab,
        tabCount: endStep.config?.tabs?.length || 0,
        tabLabels: endStep.config?.tabs?.map(t => t.label),
        configKeys: Object.keys(endStep.config || {})
      });
    }

    const latestFlow = (typeof flushPendingRef.current === 'function'
      ? flushPendingRef.current()
      : null) || flowData;

    const updatedFlowData = await flowStepsApi.updateFlowData(projectId, {
        steps: latestFlow.steps,
        connections: latestFlow.connections,
        variables: latestFlow.variables,
        settings: latestFlow.settings
    });

    setFlowData(updatedFlowData);

    if (endStep) {
      const savedEndStep = updatedFlowData.steps?.find(s => s.type === 'end');
      console.log('✅ [SAVE] End step config in response:', {
        enableMultiTab: savedEndStep?.config?.enableMultiTab,
        tabCount: savedEndStep?.config?.tabs?.length || 0,
        tabLabels: savedEndStep?.config?.tabs?.map(t => t.label),
        configKeys: Object.keys(savedEndStep?.config || {})
      });
    }

    return updatedFlowData;
  };

  // Save flow data to backend
  const handleSaveFlow = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await persistFlowData();
      console.log('Flow saved successfully!');
    } catch (err) {
      console.error('Save flow error:', err);
      setError(err.response?.data?.message || 'Failed to save flow');
    }
    setSaving(false);
  };

  // Handle flow data changes from the editor
  const handleFlowDataChange = useCallback((updatedFlowData) => {
    console.log('🔄 [FlowDesignerPage] handleFlowDataChange called:', {
      hasSteps: !!updatedFlowData?.steps,
      stepsCount: updatedFlowData?.steps?.length,
      hasConnections: !!updatedFlowData?.connections,
      connectionsCount: updatedFlowData?.connections?.length,
      hasVariables: !!updatedFlowData?.variables,
      hasSettings: !!updatedFlowData?.settings
    });
    setFlowData(updatedFlowData);
  }, []);

  const handleOpenSaveTemplate = () => {
    setTemplateForm({
      name: project?.name ? `${project.name} Template` : '',
      description: project?.description || '',
      category: 'custom',
      tags: '',
      availability: 'current_company',
      sharedCompanyIds: []
    });
    setError('');
    setSuccess('');
    setSaveTemplateOpen(true);
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name.trim()) {
      setError('Template name is required');
      return;
    }

    setSavingTemplate(true);
    setError('');
    setSuccess('');
    try {
      await persistFlowData();
      await templatesApi.saveFromProject(projectId, {
        ...templateForm,
        name: templateForm.name.trim(),
        tags: templateForm.tags
          .split(',')
          .map(tag => tag.trim())
          .filter(Boolean)
      });
      setSaveTemplateOpen(false);
      setSuccess('Flow template saved successfully.');
    } catch (err) {
      console.error('Save template error:', err);
      setError(err.response?.data?.message || 'Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleOpenLoadTemplate = async () => {
    setLoadTemplateOpen(true);
    setTemplatesLoading(true);
    setSelectedTemplateId('');
    setError('');
    setSuccess('');
    try {
      const savedTemplates = await templatesApi.listTemplates(companyId);
      setTemplates(savedTemplates);
    } catch (err) {
      console.error('Load templates error:', err);
      setError(err.response?.data?.message || 'Failed to load templates');
      setTemplates([]);
    } finally {
      setTemplatesLoading(false);
    }
  };

  const handleApplyTemplate = async () => {
    if (!selectedTemplateId) {
      setError('Select a template to import');
      return;
    }

    setApplyingTemplate(true);
    setError('');
    setSuccess('');
    try {
      await templatesApi.applyToProject(selectedTemplateId, projectId);
      const importedFlowData = await flowStepsApi.getFlowData(projectId);
      const normalizedSteps = (importedFlowData.steps || []).map(step => ({
        ...step,
        enabled: step.enabled !== undefined ? step.enabled : true
      }));

      setFlowData({
        steps: normalizedSteps,
        connections: importedFlowData.connections || [],
        variables: importedFlowData.variables || [],
        settings: importedFlowData.settings || {
          allowFileUpload: true,
          allowApiInput: false,
          maxFileSize: 20,
          supportedFileTypes: ['pdf', 'png', 'jpg', 'jpeg', 'xlsx', 'docx']
        }
      });
      setLoadTemplateOpen(false);
      setSuccess('Template imported successfully.');
    } catch (err) {
      console.error('Apply template error:', err);
      setError(err.response?.data?.message || 'Failed to import template');
    } finally {
      setApplyingTemplate(false);
    }
  };

  // Navigation handlers
  const handleBackToProject = () => {
    navigate(`/company/${companyId}/project/${projectId}`);
  };

  const handleTestFlow = () => {
    setTestFlowOpen(true);
  };

  if (loading) {
    return (
      <Box maxWidth={1200} mx="auto" mt={4} px={2}>
        <BreadcrumbsNav />
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress size={60} />
        </Box>
      </Box>
    );
  }

  if (!project) {
    return (
      <Box maxWidth={1200} mx="auto" mt={4} px={2}>
        <BreadcrumbsNav />
        <Alert severity="error">Project not found</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <AppBar position="static" elevation={1} sx={{ bgcolor: company?.primaryColor || '#667eea' }}>
        <Toolbar sx={{ minHeight: isMobile ? 56 : 64, py: isMobile ? 0.5 : 1 }}>
          <IconButton
            color="inherit"
            onClick={handleBackToProject}
            size={isMobile ? "small" : "medium"}
            sx={{ mr: isMobile ? 1 : 2 }}
          >
            <ArrowBackIcon fontSize={isMobile ? "small" : "medium"} />
          </IconButton>
          
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant={isMobile ? "subtitle1" : "h6"} component="div" noWrap>
              {isMobile ? project.name : `Flow Designer - ${project.name}`}
            </Typography>
            {!isMobile && (
              <Typography variant="body2" sx={{ opacity: 0.9 }} noWrap>
                {company?.name} • {project.description || 'No description'}
              </Typography>
            )}
          </Box>

          <Box display="flex" gap={isMobile ? 0.5 : 2} alignItems="center" flexWrap="nowrap">
            {!isMobile && (
              <Chip 
                label={project.status || 'draft'} 
                color={project.status === 'active' ? 'success' : 'default'}
                size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
              />
            )}
            
            <Button
              color="inherit"
              startIcon={isMobile ? null : <PlayIcon />}
              onClick={handleTestFlow}
              size={isMobile ? "small" : "medium"}
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.1)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                minWidth: isMobile ? 'auto' : 'inherit',
                px: isMobile ? 1 : 2
              }}
            >
              {isMobile ? <PlayIcon fontSize="small" /> : 'Test Flow'}
            </Button>

            <Button
              color="inherit"
              startIcon={isMobile ? null : <FileCopyIcon />}
              onClick={handleOpenLoadTemplate}
              disabled={templatesLoading || applyingTemplate}
              size={isMobile ? "small" : "medium"}
              sx={{
                bgcolor: 'rgba(255,255,255,0.1)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                minWidth: isMobile ? 'auto' : 'inherit',
                px: isMobile ? 1 : 2
              }}
            >
              {isMobile ? <FileCopyIcon fontSize="small" /> : 'Import Template'}
            </Button>

            <Button
              color="inherit"
              startIcon={isMobile ? null : <FileCopyIcon />}
              onClick={handleOpenSaveTemplate}
              disabled={savingTemplate}
              size={isMobile ? "small" : "medium"}
              sx={{
                bgcolor: 'rgba(255,255,255,0.1)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                minWidth: isMobile ? 'auto' : 'inherit',
                px: isMobile ? 1 : 2
              }}
            >
              {isMobile ? <FileCopyIcon fontSize="small" /> : 'Save as Template'}
            </Button>
            
            <Button
              color="inherit"
              startIcon={isMobile ? null : (saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />)}
              onClick={handleSaveFlow}
              disabled={saving}
              size={isMobile ? "small" : "medium"}
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.1)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                minWidth: isMobile ? 'auto' : 'inherit',
                px: isMobile ? 1 : 2
              }}
            >
              {isMobile ? (saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon fontSize="small" />) : (saving ? 'Saving...' : 'Save Flow')}
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Flow Editor */}
      <Box sx={{ 
        flexGrow: 1, 
        bgcolor: '#f5f5f5',
        height: 0, // Force flex child height constraint
        position: 'relative',
        overflow: 'hidden'
      }}>
        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              position: 'absolute',
              top: 8,
              left: 8,
              right: 8,
              zIndex: 1000,
              boxShadow: 2
            }}
          >
            {error}
          </Alert>
        )}
        {success && (
          <Alert
            severity="success"
            onClose={() => setSuccess('')}
            sx={{
              position: 'absolute',
              top: error ? 64 : 8,
              left: 8,
              right: 8,
              zIndex: 1000,
              boxShadow: 2
            }}
          >
            {success}
          </Alert>
        )}
        <FlowErrorBoundary>
                      <VisualFlowEditor
              flowData={flowData}
              onFlowDataChange={handleFlowDataChange}
              flushPendingRef={flushPendingRef}
              availableVariables={availableVariables}
              availableApiEndpoints={availableApiEndpoints}
              availableIntents={availableIntents}
              projectId={projectId}
              companyId={companyId}
            />
        </FlowErrorBoundary>
      </Box>

      {/* Test Flow Dialog */}
      <TestFlowDialog
        open={testFlowOpen}
        onClose={() => setTestFlowOpen(false)}
        flowData={flowData}
        availableVariables={availableVariables}
        projectId={projectId}
      />

      <Dialog open={loadTemplateOpen} onClose={() => setLoadTemplateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Import Flow Template</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} sx={{ pt: 1 }}>
            <Alert severity="warning">
              Importing a template will replace the current flow in this project.
            </Alert>
            <TextField
              select
              label="Template"
              value={selectedTemplateId}
              onChange={event => setSelectedTemplateId(event.target.value)}
              fullWidth
              disabled={templatesLoading || applyingTemplate}
              helperText={templatesLoading ? 'Loading templates...' : 'Choose a saved template to import'}
            >
              {templates.length === 0 && (
                <MenuItem value="" disabled>
                  No templates available
                </MenuItem>
              )}
              {templates.map(template => (
                <MenuItem key={template._id} value={template._id}>
                  {template.name} ({template.stepCount || 0} steps)
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLoadTemplateOpen(false)} disabled={applyingTemplate}>
            Cancel
          </Button>
          <Button
            onClick={handleApplyTemplate}
            variant="contained"
            disabled={applyingTemplate || templatesLoading || !selectedTemplateId}
            startIcon={applyingTemplate ? <CircularProgress size={16} /> : <FileCopyIcon />}
          >
            {applyingTemplate ? 'Importing...' : 'Import Template'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={saveTemplateOpen} onClose={() => setSaveTemplateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Save Flow as Template</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} sx={{ pt: 1 }}>
            <TextField
              label="Template Name"
              value={templateForm.name}
              onChange={event => setTemplateForm(prev => ({ ...prev, name: event.target.value }))}
              required
              fullWidth
              disabled={savingTemplate}
            />
            <TextField
              label="Description"
              value={templateForm.description}
              onChange={event => setTemplateForm(prev => ({ ...prev, description: event.target.value }))}
              multiline
              rows={3}
              fullWidth
              disabled={savingTemplate}
            />
            <TextField
              select
              label="Category"
              value={templateForm.category}
              onChange={event => setTemplateForm(prev => ({ ...prev, category: event.target.value }))}
              fullWidth
              disabled={savingTemplate}
            >
              <MenuItem value="invoice">Invoice</MenuItem>
              <MenuItem value="receipt">Receipt</MenuItem>
              <MenuItem value="contract">Contract</MenuItem>
              <MenuItem value="form">Form</MenuItem>
              <MenuItem value="custom">Custom</MenuItem>
            </TextField>
            <TextField
              label="Tags"
              value={templateForm.tags}
              onChange={event => setTemplateForm(prev => ({ ...prev, tags: event.target.value }))}
              helperText="Comma-separated tags"
              fullWidth
              disabled={savingTemplate}
            />
            <FormControlLabel
              control={<Checkbox checked disabled />}
              label="Owner can always use this template in the source company"
            />
            <TextField
              select
              label="Availability"
              value={templateForm.availability}
              onChange={event => setTemplateForm(prev => ({
                ...prev,
                availability: event.target.value,
                sharedCompanyIds: event.target.value === 'selected_companies' ? prev.sharedCompanyIds : []
              }))}
              fullWidth
              disabled={savingTemplate}
            >
              <MenuItem value="current_company">Current company only</MenuItem>
              <MenuItem value="all_companies">All companies</MenuItem>
              <MenuItem value="selected_companies">Selected companies</MenuItem>
            </TextField>
            {templateForm.availability === 'selected_companies' && (
              <TextField
                select
                SelectProps={{ multiple: true }}
                label="Companies"
                value={templateForm.sharedCompanyIds}
                onChange={event => setTemplateForm(prev => ({
                  ...prev,
                  sharedCompanyIds: event.target.value
                }))}
                fullWidth
                disabled={savingTemplate}
                helperText="Choose which companies can use this template"
              >
                {availableCompanies.map(companyOption => (
                  <MenuItem key={companyOption._id} value={companyOption._id}>
                    {companyOption.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveTemplateOpen(false)} disabled={savingTemplate}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveTemplate}
            variant="contained"
            disabled={savingTemplate || !templateForm.name.trim()}
            startIcon={savingTemplate ? <CircularProgress size={16} /> : <FileCopyIcon />}
          >
            {savingTemplate ? 'Saving...' : 'Save Template'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FlowDesignerPage; 